import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, Users, Megaphone, FileText, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { content } from "../data/content";
import SectionHeading from "./SectionHeading";

const COLUMN_ICONS = [Users, Megaphone, FileText];
const GAP = 20;
const two = (n) => String(n).padStart(2, "0");

const ProblemSection = () => {
  const { lang } = useLanguage();
  const t = content[lang].problem;
  const cols = t.columns;
  const total = cols.length;
  const [index, setIndex] = useState(0);
  const viewportRef = useRef(null);
  const [slideW, setSlideW] = useState(0);

  useEffect(() => {
    const update = () => {
      const el = viewportRef.current;
      if (!el) return;
      const factor = window.innerWidth >= 768 ? 0.86 : 1;
      setSlideW(el.offsetWidth * factor);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const go = useCallback((dir) => setIndex((i) => (i + dir + total) % total), [total]);

  return (
    <section id="problema" className="relative overflow-hidden py-24 md:py-32" data-testid="problem-section-container">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_72%_42%,rgba(124,231,212,0.045),transparent_50%)]" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-[1fr_1.12fr] lg:gap-12">
          {/* Left: heading + controls */}
          <div data-testid="problem-heading">
            <SectionHeading kicker={t.kicker} title={t.title} accent={t.titleAccent} body={t.body} testId="problem" />

            <div className="mt-9 flex items-center gap-4" data-testid="problem-carousel-controls">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => go(-1)}
                  aria-label="Anterior"
                  data-testid="problem-prev-button"
                  className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-white/70 transition-colors duration-200 hover:border-narwhal-teal/50 hover:text-narwhal-teal"
                >
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>
                <button
                  onClick={() => go(1)}
                  aria-label="Siguiente"
                  data-testid="problem-next-button"
                  className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-white/70 transition-colors duration-200 hover:border-narwhal-teal/50 hover:text-narwhal-teal"
                >
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <div className="ml-1 flex items-center gap-2" data-testid="problem-dots">
                {cols.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    aria-label={`Ir a la tarjeta ${i + 1}`}
                    data-testid={`problem-dot-${i}`}
                    className={`h-2 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-narwhal-teal" : "w-2 bg-white/20 hover:bg-white/40"}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right: card slider */}
          <div className="relative min-w-0" ref={viewportRef} data-testid="problem-slider">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ gap: `${GAP}px`, transform: `translateX(-${index * (slideW + GAP)}px)` }}
              >
                {cols.map((column, i) => {
                  const Icon = COLUMN_ICONS[i] || Users;
                  return (
                    <article
                      key={column.name}
                      data-testid={`problem-card-${i}`}
                      style={{ width: slideW ? `${slideW}px` : "100%" }}
                      className={`shrink-0 rounded-2xl border p-6 transition-[border-color,opacity] duration-300 md:p-8 ${
                        i === index ? "border-narwhal-teal/25 bg-gradient-to-b from-[#0d1a1c] to-[#080f11]" : "border-white/[0.07] bg-gradient-to-b from-[#0b1618] to-[#080f11] opacity-60"
                      }`}
                    >
                      <div className="flex items-baseline gap-1.5 text-sm font-medium tracking-wide">
                        <span className="text-narwhal-teal" data-testid={`problem-card-index-${i}`}>{two(i + 1)}</span>
                        <span className="text-narwhal-muted">/ {two(total)}</span>
                      </div>

                      <header className="mt-6 flex items-start gap-4">
                        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border border-narwhal-teal/20 bg-narwhal-teal/[0.08]">
                          <Icon className="h-6 w-6 text-narwhal-teal" strokeWidth={1.6} aria-hidden="true" />
                        </span>
                        <div className="min-w-0 pt-0.5">
                          <h3 className="text-lg font-semibold leading-snug text-white md:text-xl" data-testid={`problem-card-title-${i}`}>{column.name}</h3>
                          {column.caption && <p className="mt-1 text-sm leading-snug text-narwhal-muted">{column.caption}</p>}
                        </div>
                      </header>

                      <div className="mt-6 h-px w-full bg-gradient-to-r from-narwhal-teal/25 via-white/[0.06] to-transparent" />

                      <ul className="mt-6 flex flex-col gap-4">
                        {column.points.map((point, pointIndex) => (
                          <li
                            key={point}
                            className="flex items-center gap-3 text-sm leading-relaxed text-narwhal-muted"
                            data-testid={`problem-card-${i}-item-${pointIndex}`}
                          >
                            <X className="h-4 w-4 shrink-0 text-narwhal-teal" strokeWidth={2.4} aria-hidden="true" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
